'use client'

import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'
import { cn } from '@/utils'

export interface DividerProps extends VariantProps<typeof dividerVariants> {
  /**
   * Rendered width, applied as an inline style. Numbers are treated as pixels
   * (`24` becomes `24px`); strings are passed through, so any CSS length works.
   * Defaults to `100%`.
   */
  width?: string | number
  /** Additional CSS classes, merged after the variant classes. */
  className?: string
}

const dividerVariants = cva('h-px flex-shrink-0', {
  variants: {
    pattern: {
      light: 'bg-gray-300 border-gray-300',
      dark: 'bg-gray-500 border-gray-500',
    },
    state: {
      solid: 'h-px',
      dashed: 'border border-dashed bg-white',
      dotted: 'border border-dotted bg-white',
    },
  },
  defaultVariants: {
    pattern: 'light',
    state: 'solid',
  },
})

export const Divider: React.FC<DividerProps> = ({
  pattern,
  state,
  width = '100%',
  className,
  ...props
}) => {
  return (
    <div
      className={cn(dividerVariants({ pattern, state }), className)}
      style={{ width: typeof width === 'number' ? `${width}px` : width }}
      data-testid="divider"
      {...props}
    />
  )
}
